import { useState, useEffect } from "react";
import { Link, useParams } from "react-router";
import { fetchWorkshopSessionById, submitWorkshopSubscription, insertWorkshopSubscription, type WorkshopSessionWithCount } from "./supabase";
import { TurnstileWidget } from "./TurnstileWidget";
import { useTheme } from "./ThemeInjector";
import { toast } from "sonner";

function turnstileThemeFromBg(hex: string): "light" | "dark" {
  const h = hex.replace(/^#/, "");
  if (h.length !== 6 && h.length !== 8) return "light";
  const r = parseInt(h.slice(0, 2), 16) / 255;
  const g = parseInt(h.slice(2, 4), 16) / 255;
  const b = parseInt(h.slice(4, 6), 16) / 255;
  const luminance = 0.299 * r + 0.587 * g + 0.114 * b;
  return luminance < 0.5 ? "dark" : "light";
}

export function WorkshopReservePage() {
  const { sessionId } = useParams<{ sessionId: string }>();
  const theme = useTheme();
  const [session, setSession] = useState<WorkshopSessionWithCount | null>(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);
  const [turnstileKey, setTurnstileKey] = useState(0);
  const [turnstileErrorOccurred, setTurnstileErrorOccurred] = useState(false);
  const [formData, setFormData] = useState({
    first_name: "",
    last_name: "",
    email: "",
    phone: "",
    company: "",
    vat_number: "",
    notes: "",
    marketing_opt_in: false,
    privacy_policy_accepted: false,
    terms_accepted: false,
    website: "", // honeypot
  });

  useEffect(() => {
    if (!sessionId) {
      setLoading(false);
      return;
    }
    fetchWorkshopSessionById(sessionId).then(({ data, error }) => {
      setLoading(false);
      if (error || !data) {
        setSession(null);
        return;
      }
      if (data.status !== "scheduled" && data.status !== "full") {
        setSession(null);
        return;
      }
      if (!data.is_visible) setSession(null);
      else setSession(data);
    });
  }, [sessionId]);

  const turnstileSiteKey = (import.meta.env.VITE_TURNSTILE_SITE_KEY ?? import.meta.env.VITE_TURNSTILE_SITEKEY) as string | undefined;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!sessionId || !session) return;
    if (!formData.first_name.trim()) {
      toast.error("First name is required.");
      return;
    }
    if (!formData.last_name.trim()) {
      toast.error("Last name is required.");
      return;
    }
    if (!formData.privacy_policy_accepted || !formData.terms_accepted) {
      toast.error("Please accept the privacy policy and terms.");
      return;
    }
    if (turnstileSiteKey && !turnstileToken) {
      toast.error("Please complete the verification check.");
      return;
    }
    // Honeypot: bots often fill hidden fields
    if (formData.website.trim() !== "") {
      toast.error("Invalid submission.");
      return;
    }
    const maxCap = session.max_capacity ?? 0;
    const count = Number(session.subscriber_count ?? 0);
    const isFull = maxCap > 0 && count >= maxCap;
    setSubmitting(true);

    const useEdgeFunction = Boolean(turnstileSiteKey && turnstileToken);
    if (useEdgeFunction) {
      const result = await submitWorkshopSubscription({
        session_id: sessionId,
        email: formData.email,
        website: formData.website || undefined,
        first_name: formData.first_name.trim() || null,
        last_name: formData.last_name.trim() || null,
        phone: formData.phone?.trim() || null,
        company: formData.company?.trim() || null,
        vat_number: formData.vat_number?.trim() || null,
        notes: formData.notes?.trim() || null,
        marketing_opt_in: formData.marketing_opt_in,
        privacy_policy_accepted: true,
        terms_accepted: true,
        source: "website",
        status: isFull ? "waitlist" : "subscribed",
        turnstile_token: turnstileToken ?? undefined,
      });
      setSubmitting(false);
      if (!result.ok) {
        toast.error(result.error || "Could not complete. Please try again.");
        return;
      }
    } else {
      const { error } = await insertWorkshopSubscription({
        session_id: sessionId,
        email: formData.email,
        first_name: formData.first_name.trim() || null,
        last_name: formData.last_name.trim() || null,
        phone: formData.phone?.trim() || null,
        company: formData.company?.trim() || null,
        vat_number: formData.vat_number?.trim() || null,
        notes: formData.notes?.trim() || null,
        marketing_opt_in: formData.marketing_opt_in,
        privacy_policy_accepted: true,
        terms_accepted: true,
        source: "website",
        status: isFull ? "waitlist" : "subscribed",
      });
      setSubmitting(false);
      if (error) {
        if (error.message?.includes("unique") || error.message?.includes("duplicate")) {
          toast.error("This email is already registered or on the waiting list for this session.");
        } else {
          toast.error(error.message || "Could not complete. Please try again.");
        }
        return;
      }
    }

    toast.success(isFull ? "You're on the waiting list. We'll notify you if a spot opens up." : "Your spot is reserved. We'll be in touch.");
    setFormData({
      first_name: "",
      last_name: "",
      email: "",
      phone: "",
      company: "",
      vat_number: "",
      notes: "",
      marketing_opt_in: false,
      privacy_policy_accepted: false,
      terms_accepted: false,
      website: "",
    });
    setTurnstileToken(null);
    setTurnstileKey((k) => k + 1);
  };

  if (loading) {
    return (
      <main className="min-h-screen py-24 px-6">
        <p style={{ color: theme.muted }}>Loading…</p>
      </main>
    );
  }

  if (!session) {
    return (
      <main className="min-h-screen py-24 px-6 max-w-2xl mx-auto">
        <h1 style={{ fontFamily: theme.fontHeading, color: theme.fg }}>Session not found</h1>
        <p style={{ color: theme.muted, marginTop: 8 }}>This workshop session may have been cancelled or is no longer available.</p>
        <Link to="/workshops" className="inline-block mt-6 underline" style={{ color: theme.primary }}>Back to workshops</Link>
      </main>
    );
  }

  const title = session.title_override || session.template_title;
  const location = session.location_override || session.template_location;
  const maxCap = session.max_capacity ?? 0;
  const count = Number(session.subscriber_count ?? 0);
  const isFull = maxCap > 0 && count >= maxCap;

  return (
    <main className="min-h-screen py-24 px-6" style={{ backgroundColor: theme.bg }}>
      <div className="max-w-2xl mx-auto">
        <Link to="/workshops" className="text-sm underline mb-6 inline-block" style={{ color: theme.primary }}>← Back to workshops</Link>
        <h1 className="text-2xl mb-2" style={{ fontFamily: theme.fontHeading, color: theme.fg }}>Reserve a spot</h1>
        <p className="mb-6" style={{ color: theme.muted, fontSize: 14 }}>
          {title} · {new Date(session.starts_at).toLocaleString(undefined, { dateStyle: "long", timeStyle: "short" })}
          {location && ` · ${location}`}
        </p>

        {isFull ? (
          <p style={{ color: theme.muted }}>This session is full. Please choose another date.</p>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            <div
              style={{
                position: "absolute",
                width: "1px",
                height: "1px",
                overflow: "hidden",
              }}
              aria-hidden="true"
            >
              <label htmlFor="workshop-website">Website</label>
              <input
                id="workshop-website"
                type="text"
                name="website"
                autoComplete="off"
                tabIndex={-1}
                value={formData.website}
                onChange={(e) => setFormData({ ...formData, website: e.target.value })}
              />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs uppercase tracking-wider mb-1" style={{ color: theme.muted }}>First name *</label>
                <input type="text" required value={formData.first_name} onChange={(e) => setFormData({ ...formData, first_name: e.target.value })} className="w-full px-4 py-2.5 border rounded-lg" style={{ borderColor: theme.border, backgroundColor: theme.card, color: theme.fg }} />
              </div>
              <div>
                <label className="block text-xs uppercase tracking-wider mb-1" style={{ color: theme.muted }}>Last name *</label>
                <input type="text" required value={formData.last_name} onChange={(e) => setFormData({ ...formData, last_name: e.target.value })} className="w-full px-4 py-2.5 border rounded-lg" style={{ borderColor: theme.border, backgroundColor: theme.card, color: theme.fg }} />
              </div>
            </div>
            <div>
              <label className="block text-xs uppercase tracking-wider mb-1" style={{ color: theme.muted }}>Email *</label>
              <input type="email" required value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} className="w-full px-4 py-2.5 border rounded-lg" style={{ borderColor: theme.border, backgroundColor: theme.card, color: theme.fg }} />
            </div>
            <div>
              <label className="block text-xs uppercase tracking-wider mb-1" style={{ color: theme.muted }}>Phone</label>
              <input type="tel" value={formData.phone} onChange={(e) => setFormData({ ...formData, phone: e.target.value })} className="w-full px-4 py-2.5 border rounded-lg" style={{ borderColor: theme.border, backgroundColor: theme.card, color: theme.fg }} />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs uppercase tracking-wider mb-1" style={{ color: theme.muted }}>Company</label>
                <input type="text" value={formData.company} onChange={(e) => setFormData({ ...formData, company: e.target.value })} className="w-full px-4 py-2.5 border rounded-lg" style={{ borderColor: theme.border, backgroundColor: theme.card, color: theme.fg }} />
              </div>
              <div>
                <label className="block text-xs uppercase tracking-wider mb-1" style={{ color: theme.muted }}>VAT number</label>
                <input type="text" value={formData.vat_number} onChange={(e) => setFormData({ ...formData, vat_number: e.target.value })} className="w-full px-4 py-2.5 border rounded-lg" style={{ borderColor: theme.border, backgroundColor: theme.card, color: theme.fg }} />
              </div>
            </div>
            <div>
              <label className="block text-xs uppercase tracking-wider mb-1" style={{ color: theme.muted }}>Notes</label>
              <textarea rows={3} value={formData.notes} onChange={(e) => setFormData({ ...formData, notes: e.target.value })} className="w-full px-4 py-2.5 border rounded-lg resize-y" style={{ borderColor: theme.border, backgroundColor: theme.card, color: theme.fg }} />
            </div>
            <div className="space-y-3">
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={formData.privacy_policy_accepted} onChange={(e) => setFormData({ ...formData, privacy_policy_accepted: e.target.checked })} required className="rounded border" style={{ borderColor: theme.border }} />
                <span style={{ fontSize: 14, color: theme.fg }}>I accept the <Link to="/policies" className="underline" target="_blank" rel="noopener">privacy policy</Link> *</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={formData.terms_accepted} onChange={(e) => setFormData({ ...formData, terms_accepted: e.target.checked })} required className="rounded border" style={{ borderColor: theme.border }} />
                <span style={{ fontSize: 14, color: theme.fg }}>I accept the terms *</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={formData.marketing_opt_in} onChange={(e) => setFormData({ ...formData, marketing_opt_in: e.target.checked })} className="rounded border" style={{ borderColor: theme.border }} />
                <span style={{ fontSize: 14, color: theme.fg }}>Send me updates and offers</span>
              </label>
            </div>
            {turnstileSiteKey ? (
              <div className="space-y-2">
                <TurnstileWidget
                  key={turnstileKey}
                  siteKey={turnstileSiteKey}
                  theme={turnstileThemeFromBg(theme.bg)}
                  onSuccess={(token) => {
                    setTurnstileToken(token);
                    setTurnstileErrorOccurred(false);
                  }}
                  onExpire={() => setTurnstileToken(null)}
                  onError={() => {
                    setTurnstileToken(null);
                    setTurnstileErrorOccurred(true);
                  }}
                />
                {turnstileErrorOccurred && (
                  <p style={{ fontSize: 13, color: theme.muted }}>
                    Verification failed.{" "}
                    <button type="button" className="underline" onClick={() => { setTurnstileErrorOccurred(false); setTurnstileKey((k) => k + 1); }}>
                      Try again
                    </button>
                  </p>
                )}
              </div>
            ) : null}
            <button
              type="submit"
              disabled={submitting || (Boolean(turnstileSiteKey) && !turnstileToken)}
              className="w-full py-3 rounded-lg text-white uppercase tracking-wider disabled:opacity-50"
              style={{ backgroundColor: theme.primary }}
            >
              {submitting ? (isFull ? "Adding to list…" : "Reserving…") : isFull ? "Join waiting list" : "Reserve my spot"}
            </button>
          </form>
        )}
      </div>
    </main>
  );
}
